import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Grid, Tooltip, Typography } from '@mui/material';
import {
  ChildCare as ChildCareIcon,
  Notifications as NotificationsIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  EventAvailable as EventAvailableIcon,
  ScheduleSend as ScheduleSendIcon,
  MarkChatRead as MarkChatReadIcon,
  Sync as SyncIcon,
  ErrorOutline as DeliveryFailedIcon,
  HourglassEmpty as AwaitingIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { dashboardService } from '../services';
import { EMPTY_DASHBOARD_STATS, type DashboardStats } from '../services/dashboardService';
import { DISPLAY_CARRIERS } from '../config/carriers';
import { getApiErrorMessage } from '../lib/apiError';
import { MetricCard, type MetricBreakdownRow } from '../components/shared/MetricCard';
import { DailySmsChart } from '../components/dashboard/DailySmsChart';
import { RecipientsByCarrierCard } from '../components/dashboard/RecipientsByCarrierCard';
import { SmsByCarrierCard } from '../components/dashboard/SmsByCarrierCard';
import { useDailyChart } from '../hooks/useDailyChart';
import { useManualRuns } from '../hooks/useManualRuns';
import { useRemindersByVaccine } from '../hooks/useRemindersByVaccine';
import { RemindersByVaccineCard } from '../components/dashboard/RemindersByVaccineCard';

export const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<DashboardStats>(EMPTY_DASHBOARD_STATS);
  const chart = useDailyChart();

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setStats(await dashboardService.getStats());
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load dashboard data'));
    } finally {
      setLoading(false);
    }
  }, []);

  const vaccines = useRemindersByVaccine();
  const reloadVaccines = vaccines.reload;
  const runs = useManualRuns(
    useCallback(() => {
      loadDashboardData();
      reloadVaccines();
    }, [loadDashboardData, reloadVaccines])
  );
  const busy = runs.syncing || runs.scheduling;

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // From dedicated counts, not smsByCarrier: its 'failed' bucket also holds
  // gateway rejections that were never dispatched, which would overshoot the total.
  const smsOutcomes = useMemo<MetricBreakdownRow[]>(
    () => [
      {
        label: 'Delivered',
        description: 'the network confirmed it reached the handset',
        value: stats.smsDelivered,
        color: 'success',
        icon: <CheckCircleIcon />,
      },
      {
        label: 'Failed',
        description: 'sent, but the network reported it was not delivered',
        value: stats.smsFailedAfterDispatch,
        color: 'error',
        icon: <DeliveryFailedIcon />,
      },
      {
        // info, not warning: warning already means Pending (queued) on the carrier card.
        label: 'Awaiting confirmation',
        description: 'sent, with no delivery report back yet',
        value: stats.smsAwaitingConfirmation,
        color: 'info',
        icon: <AwaitingIcon />,
      },
    ],
    [stats.smsDelivered, stats.smsFailedAfterDispatch, stats.smsAwaitingConfirmation]
  );

  // The server buckets a recipient as 'unknown' exactly when the phone is unusable,
  // so the carrier buckets already give the reachable count.
  const textableRecipients = useMemo(
    () => DISPLAY_CARRIERS.reduce((sum, c) => sum + stats.carrierBreakdown[c.key], 0),
    [stats.carrierBreakdown]
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" gutterBottom fontWeight={600}>
            Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Welcome back, {user?.firstName}!
          </Typography>
        </Box>
        {user?.role === 'ADMIN' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Manual trigger - system auto-syncs daily">
                <Button
                  variant="outlined"
                  startIcon={runs.scheduling ? <CircularProgress size={18} /> : <NotificationsIcon />}
                  onClick={runs.runSchedule}
                  disabled={busy}
                  size="small"
                >
                  {runs.scheduling ? 'Scheduling...' : 'Schedule Reminders'}
                </Button>
              </Tooltip>
              <Tooltip title="Manual trigger - system auto-syncs daily from DHIS2">
                <Button
                  variant="contained"
                  startIcon={runs.syncing ? <CircularProgress size={18} color="inherit" /> : <SyncIcon />}
                  onClick={runs.runSync}
                  disabled={busy}
                  size="small"
                >
                  {runs.syncing ? 'Syncing...' : 'Sync DHIS2'}
                </Button>
              </Tooltip>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right', maxWidth: 560, fontSize: '0.75rem' }}>
              Automated sync runs daily. Reminders are automatically sent. Use these buttons only
              to trigger manual runs.
            </Typography>
          </Box>
        )}
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {runs.result && (
        <Alert severity={runs.result.severity} sx={{ mb: 3 }} onClose={runs.clearResult}>
          {runs.result.message}
        </Alert>
      )}

      {/* Metrics Cards - Row 1 */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            title="Total Children"
            value={stats.totalRecipients.toLocaleString()}
            icon={<ChildCareIcon />}
            color="primary"
            info="Children synced from DHIS2, including those whose phone number could not be used."
            subtext={
              stats.totalRecipients === 0
                ? 'Sync from DHIS2 to get started'
                : `${textableRecipients.toLocaleString()} can receive SMS (${Math.round(
                    (textableRecipients / stats.totalRecipients) * 100
                  )}%)`
            }
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            title="Upcoming Visits"
            value={stats.upcomingVisits.toLocaleString()}
            icon={<EventAvailableIcon />}
            color="success"
            info="Visits that have not happened yet and still have at least one reminder waiting to go out."
            subtext="Each visit counted once"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            title="Pending Reminders"
            value={stats.pendingReminders.toLocaleString()}
            icon={<ScheduleSendIcon />}
            color="info"
            info="Reminders queued and not sent yet, including those whose send date has not arrived."
            subtext="Queued to send"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Metrics Cards - Row 2: what actually went out */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={6}>
          <MetricCard
            title="SMS Sent"
            value={stats.totalRemindersSent.toLocaleString()}
            icon={<MarkChatReadIcon />}
            color="success"
            info="Reminder messages sent, counting messages rather than visits."
            loading={loading}
            breakdown={smsOutcomes}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <MetricCard
            title="Visits Reminded"
            value={stats.visitsWithSms.toLocaleString()}
            icon={<SendIcon />}
            color="primary"
            subtext="A reminder reached the network"
            info="Past visits where at least one reminder was sent and not reported undelivered."
            loading={loading}
          />
        </Grid>
      </Grid>

      <DailySmsChart
        days={chart.days}
        onDaysChange={chart.setDays}
        data={chart.data}
        loading={chart.loading}
        error={chart.error}
      />
      <RemindersByVaccineCard data={vaccines.data} loading={vaccines.loading} error={vaccines.error} />
      <RecipientsByCarrierCard carrierBreakdown={stats.carrierBreakdown} loading={loading} />
      <SmsByCarrierCard smsByCarrier={stats.smsByCarrier} loading={loading} />
    </Box>
  );
};
